import { Suspense, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'

import { useUserReviews } from '../../../api/marketplace/use-user-reviews'
import { ReviewStar } from '../../../assets/review-star'
import NoReviews from '../empty/no-reviews'
import SectionTitle from '../ui/section-title'
import ViewAllButton from '../ui/view-all-button'

import PublicPageReviewCard from './public-page-review-card'
import PublicPageReviewListSkeleton from './public-page-review-list-skeleton'

// const PLACEHOLDER_REVIEW = {
//   reviewerFullName: 'Anonymous',
//   serviceDescription: 'Mortgage',
//   locationZipcode: '10001',
//   description:
//     "I honestly couldn't have asked for a better person to guide me through the mortgage process than Armando!",
//   rating: 5,
//   date: '2021-01-01',
// }

type ReviewListProps = {
  userId: number
  username: string
  onGoBack?: () => void
  showAll?: boolean
  mobile?: boolean
  firstName: string
}

const ReviewListContent = ({ userId, username, onGoBack, showAll = false, mobile, firstName }: ReviewListProps) => {
  const navigate = useNavigate()
  const { data } = useUserReviews(userId)

  const reviewStatus = useMemo(() => {
    return data?.totalReviews ? (
      <div className="flex items-center gap-2 text-base font-semibold text-muted">
        <ReviewStar size={13} pattern="gold" />
        <span>{data?.averageRating || 0}</span>
        <span>&bull;</span>
        <span>
          {data?.totalReviews || 0} review{data?.totalReviews === 1 ? '' : 's'}
        </span>
      </div>
    ) : (
      <div className="text-sm font-semibold italic text-muted lg:text-base">No reviews</div>
    )
  }, [data])

  const reviews = useMemo(() => {
    return data?.reviews || []
  }, [data])

  return (
    <section className="mb-6">
      <SectionTitle
        onGoBack={onGoBack}
        title={mobile ? reviewStatus : 'Reviews'}
        rightContent={mobile ? null : reviewStatus}
      />
      <div className="mt-4 grid grid-cols-[repeat(auto-fill,minmax(350px,1fr))] gap-4">
        {reviews?.map((review) => (
          <PublicPageReviewCard key={review.serviceDescription} review={review} />
        ))}
      </div>
      {reviews?.length === 0 && <NoReviews firstName={firstName} />}
      {!showAll && reviews?.length > 0 && (
        <ViewAllButton
          text="Show all reviews"
          onClick={() => navigate(paths.users.publicProfile.reviews.getHref(username))}
        />
      )}
    </section>
  )
}

const ReviewList = ({ userId, username, onGoBack, showAll = false, mobile, firstName }: ReviewListProps) => {
  return (
    <Suspense fallback={<PublicPageReviewListSkeleton />}>
      <ReviewListContent
        userId={userId}
        username={username}
        onGoBack={onGoBack}
        showAll={showAll}
        mobile={mobile}
        firstName={firstName}
      />
    </Suspense>
  )
}

export default ReviewList
